import { Injectable } from '@angular/core'
import { ClaimDocumentType, ClaimRequestRequiredDocuments } from '../interfaces/claim-request-required-documents'
import { ClaimPlaceType, ClaimProductType, ClaimType } from '../interfaces/classification-types'
import { TranslateService } from '@ngx-translate/core'
import { ClaimDocument, ClaimDocumentation } from '../interfaces/claim.interface'
import { ClaimDocumentationMappingsService } from './claim-documentation-mappings.service'
import { EnglishTranslateService } from './english-translate.service'

@Injectable({
    providedIn: 'root'
})
export class ClaimDocumentationService {

    constructor(
        private translate: TranslateService,
        private engTranslate: EnglishTranslateService,
        private docMapping: ClaimDocumentationMappingsService
    ) {
    }

    getRequiredDocumentation(
        claimDocumentation: ClaimDocumentation,
        productType?: ClaimProductType,
        claimType?: ClaimType,
        claimPlace?: ClaimPlaceType,
        hasWarranty?: boolean,
        isSilestone?: boolean,
        totalAmount?: number,
        hasMesh?: boolean,
        language: string = 'en'
        ): ClaimRequestRequiredDocuments
    {

        let requiredDocuments = <ClaimRequestRequiredDocuments>{
            checks: [],
            documents: [],
            additionalInfo: []
        }

        totalAmount = totalAmount || 0

        if (claimType === 'visual') {
            requiredDocuments = {
                checks: [
                    {
                        id: 'tone_variation',
                        label: this.translate.instant('create-claim.documentation.tone_variation'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.tone_variation'),
                        requiresPrevious: false,
                        isChecked: this.findCheckOfType('tone_variation', claimDocumentation.checks)
                    }, {
                        id: 'is_sample',
                        label: this.translate.instant('create-claim.documentation.is_sample'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.is_sample'),
                        requiresPrevious: true,
                        isChecked: this.findCheckOfType('is_sample', claimDocumentation.checks)
                    }
                ],
                documents: [
                    {
                        id: 'purchase_invoice',
                        label: this.translate.instant('create-claim.documentation.purchase_invoice'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.purchase_invoice'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: this.shouldAskPurchaseOrder(hasWarranty, claimPlace),
                        document: this.findDocumentOfType('purchase_invoice', claimDocumentation.documents)
                    }, {
                        id: 'defect_picture',
                        label: this.translate.instant('create-claim.documentation.defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.defect_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('defect_picture', claimDocumentation.documents)
                    }, {
                        id: 'comparison_defect_picture',
                        label: this.translate.instant('create-claim.documentation.comparison_defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.comparison_defect_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('comparison_defect_picture', claimDocumentation.documents)
                    }, {
                        id: 'comparison_tone_defect_picture',
                        label: this.translate.instant('create-claim.documentation.comparison_tone_defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.comparison_tone_defect_picture'),
                        isRequired: true,
                        requiresChecked: ['tone_variation'],
                        requiresUnchecked: ['is_sample'],
                        document: this.findDocumentOfType('comparison_tone_defect_picture', claimDocumentation.documents)
                    }, {
                        id: 'material_identifier_picture',
                        label: this.translate.instant('create-claim.documentation.material_identifier_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.material_identifier_picture'),
                        isRequired: false,
                        requiresChecked: ['tone_variation'],
                        requiresUnchecked: ['is_sample'],
                        document: this.findDocumentOfType('material_identifier_picture', claimDocumentation.documents)
                    }, {
                        id: 'comparison_tone_sample_defect_picture',
                        label: this.translate.instant('create-claim.documentation.comparison_tone_sample_defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.comparison_tone_sample_defect_picture'),
                        isRequired: true,
                        requiresChecked: ['tone_variation', 'is_sample'],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('comparison_tone_sample_defect_picture', claimDocumentation.documents)
                    }, {
                        id: 'back_face_sample_picture',
                        label: this.translate.instant('create-claim.documentation.back_face_sample_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.back_face_sample_picture'),
                        isRequired: true,
                        requiresChecked: ['tone_variation', 'is_sample'],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('back_face_sample_picture', claimDocumentation.documents)
                    }
                ],
                additionalInfo: []
            }
        } else if (productType === 'professional') {
            requiredDocuments = {
                checks: [],
                documents: [
                    {
                        id: 'defect_detail_picture',
                        label: this.translate.instant('create-claim.documentation.defect_detail_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.defect_detail_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('defect_detail_picture', claimDocumentation.documents)
                    }
                ],
                additionalInfo: [
                    {
                        id: 'origin_of_the_defect',
                        label: this.translate.instant('create-claim.documentation.extra_information_about_the_defect'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.extra_information_about_the_defect'),
                        isRequired: true,
                        value: this.findValueOfType('origin_of_the_defect', claimDocumentation.additionalInfo)
                    }
                ]
            }
        } else if(claimType === 'wrong_order'){
            requiredDocuments = {
                checks: [
                    {
                        id: 'is_it_measurement_error',
                        label: this.translate.instant('create-claim.documentation.is_it_measurement_error'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.is_it_measurement_error'),
                        requiresPrevious: false,
                        isChecked: this.findCheckOfType('is_it_measurement_error', claimDocumentation.checks)
                    },
                    {
                        id: 'is_it_warp',
                        label: this.translate.instant('create-claim.documentation.is_it_warp'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.is_it_warp'),
                        requiresPrevious: false,
                        isChecked: this.findCheckOfType('is_it_warp', claimDocumentation.checks)
                    }
                ],
                documents: [
                    {
                        id: 'purchase_invoice',
                        label: this.translate.instant('create-claim.documentation.purchase_invoice'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.purchase_invoice'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: this.shouldAskPurchaseOrder(hasWarranty, claimPlace),
                        document: this.findDocumentOfType('purchase_invoice', claimDocumentation.documents)
                    }, {
                        id: 'reported_problem_picture',
                        label: this.translate.instant('create-claim.documentation.defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.defect_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('reported_problem_picture', claimDocumentation.documents)
                    }, {
                        id: 'measure_error_picture',
                        label: this.translate.instant('create-claim.documentation.measure_error_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.measure_error_picture'),
                        isRequired: true,
                        requiresChecked: ['is_it_measurement_error'],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('measure_error_picture', claimDocumentation.documents)
                    }, {
                        id: 'warp_picture',
                        label: this.translate.instant('create-claim.documentation.warp_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.warp_picture'),
                        isRequired: true,
                        requiresChecked: ['is_it_warp'],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('warp_picture', claimDocumentation.documents)
                    }

                ],
                additionalInfo: [
                    {
                        id: 'is_a_reference_missing',
                        label: this.translate.instant('create-claim.documentation.is_a_reference_missing'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.is_a_reference_missing'),
                        isRequired: false,
                        value: this.findValueOfType('is_a_reference_missing', claimDocumentation.additionalInfo),
                        shouldShow: productType === 'cts' || productType === 'standard'
                    },
                ]
            }
        } else if(claimType === 'installation'){
            requiredDocuments = {
                checks: [
                    {
                        id: 'is_it_a_break',
                        label: this.translate.instant('create-claim.documentation.is_it_a_break'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.is_it_a_break'),
                        requiresPrevious: false,
                        isChecked: this.findCheckOfType('is_it_a_break', claimDocumentation.checks)
                    }, {
                        id: 'is_it_from_a_cutout',
                        label: this.translate.instant('create-claim.documentation.is_it_from_a_cutout'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.is_it_from_a_cutout'),
                        requiresPrevious: false,
                        requiresSpecificQuestion: 'is_it_a_break',
                        isChecked: this.findCheckOfType('is_it_from_a_cutout', claimDocumentation.checks)
                    }, {
                        id: 'is_it_overhang_zone',
                        label: this.translate.instant('create-claim.documentation.is_it_overhang_zone'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.is_it_overhang_zone'),
                        requiresPrevious: false,
                        requiresSpecificQuestion: 'is_it_a_break',
                        additionalInfoId: 'overhang_dimensions',
                        additionalInfoLabel: this.translate.instant('create-claim.documentation.provide_overhang_dimensions'),
                        additionalInfoEnglishLabel: this.engTranslate.instant('create-claim.documentation.provide_overhang_dimensions'),
                        additionalInfoValue: this.findAdditionalValueOfType('is_it_overhang_zone', claimDocumentation.checks),
                        isChecked: this.findCheckOfType('is_it_overhang_zone', claimDocumentation.checks)
                    }, {
                        id: 'did_break_happen_during_installation',
                        label: this.translate.instant('create-claim.documentation.did_break_happen_during_installation'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.did_break_happen_during_installation'),
                        requiresPrevious: false,
                        requiresSpecificQuestion: 'is_it_a_break',
                        isChecked: this.findCheckOfType('did_break_happen_during_installation', claimDocumentation.checks)
                    }
                ],
                documents: [
                    {
                        id: 'purchase_invoice',
                        label: this.translate.instant('create-claim.documentation.purchase_invoice'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.purchase_invoice'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: this.shouldAskPurchaseOrder(hasWarranty, claimPlace),
                        document: this.findDocumentOfType('purchase_invoice', claimDocumentation.documents)
                    }, {
                        id: 'defect_picture',
                        label: this.translate.instant('create-claim.documentation.defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.defect_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('defect_picture', claimDocumentation.documents)
                    }, {
                        id: 'defect_detail_picture',
                        label: this.translate.instant('create-claim.documentation.defect_detail_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.defect_detail_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('defect_detail_picture', claimDocumentation.documents)
                    }, {
                        id: 'blueprint_with_measures_picture',
                        label: this.translate.instant('create-claim.documentation.blueprint_with_measures_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.blueprint_with_measures_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('blueprint_with_measures_picture', claimDocumentation.documents)
                    }, {
                        id: 'installation_level_picture',
                        label: this.translate.instant('create-claim.documentation.installation_level_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.installation_level_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('installation_level_picture', claimDocumentation.documents)
                    }, {
                        id: 'socket_picture',
                        label: this.translate.instant('create-claim.documentation.socket_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.socket_picture'),
                        isRequired: true,
                        requiresChecked: ['is_it_a_break', 'is_it_from_a_cutout'],
                        requiresUnchecked: [],
                        additionalInfoLabel: this.translate.instant('create-claim.documentation.socket_picture_description'),
                        document: this.findDocumentOfType('socket_picture', claimDocumentation.documents)
                    }, {
                        id: 'supports_picture',
                        label: this.translate.instant('create-claim.documentation.supports_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.supports_picture'),
                        isRequired: true,
                        requiresChecked: ['is_it_a_break'],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('supports_picture', claimDocumentation.documents)
                    }, {
                        id: 'support_under_overhang_picture',
                        label: this.translate.instant('create-claim.documentation.support_under_overhang_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.support_under_overhang_picture'),
                        isRequired: true,
                        requiresChecked: ['is_it_a_break', 'is_it_overhang_zone'],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('support_under_overhang_picture', claimDocumentation.documents)
                    }, {
                        id: 'manipulation_tools_picture',
                        label: this.translate.instant('create-claim.documentation.manipulation_tools_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.manipulation_tools_picture'),
                        isRequired: true,
                        requiresChecked: ['is_it_a_break', 'did_break_happen_during_installation'],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('manipulation_tools_picture', claimDocumentation.documents)
                    }, {
                        id: 'thermal_shock_picture',
                        label: this.translate.instant('create-claim.documentation.thermal_shock_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.thermal_shock_picture'),
                        isRequired: true,
                        requiresChecked: ['is_it_a_break'],
                        requiresUnchecked: [],
                        shouldShow: isSilestone,
                        document: this.findDocumentOfType('thermal_shock_picture', claimDocumentation.documents)
                    }, {
                        id: 'insulator_picture',
                        label: this.translate.instant('create-claim.documentation.insulator_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.insulator_picture'),
                        isRequired: true,
                        requiresChecked: ['is_it_a_break'],
                        requiresUnchecked: [],
                        shouldShow: isSilestone,
                        document: this.findDocumentOfType('insulator_picture', claimDocumentation.documents)
                    }, {
                        id: 'installation_picture',
                        label: this.translate.instant('create-claim.documentation.installation_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.installation_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: claimPlace === 'installation',
                        document: this.findDocumentOfType('installation_picture', claimDocumentation.documents)
                    }
                ],
                additionalInfo: []
            }
        } else if(claimType === 'superficial') {
            requiredDocuments = {
                checks: [],
                documents: [
                    {
                        id: 'purchase_invoice',
                        label: this.translate.instant('create-claim.documentation.purchase_invoice'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.purchase_invoice'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: this.shouldAskPurchaseOrder(hasWarranty, claimPlace),
                        document: this.findDocumentOfType('purchase_invoice', claimDocumentation.documents)
                    }, {
                        id: 'defect_picture',
                        label: this.translate.instant('create-claim.documentation.defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.defect_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('defect_picture', claimDocumentation.documents)
                    }, {
                        id: 'comparison_defect_picture',
                        label: this.translate.instant('create-claim.documentation.comparison_defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.comparison_defect_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('comparison_defect_picture', claimDocumentation.documents)
                    }
                ],
                additionalInfo: []
            }
        } else if(claimType === 'impurities') {
            requiredDocuments = {
                checks: [],
                documents: [
                    {
                        id: 'purchase_invoice',
                        label: this.translate.instant('create-claim.documentation.purchase_invoice'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.purchase_invoice'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: this.shouldAskPurchaseOrder(hasWarranty, claimPlace),
                        document: this.findDocumentOfType('purchase_invoice', claimDocumentation.documents)
                    }, {
                        id: 'defect_picture',
                        label: this.translate.instant('create-claim.documentation.defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.defect_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('defect_picture', claimDocumentation.documents)
                    }, {
                        id: 'comparison_defect_picture',
                        label: this.translate.instant('create-claim.documentation.comparison_defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.comparison_defect_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('comparison_defect_picture', claimDocumentation.documents)
                    }
                ],
                additionalInfo: [
                    {
                        id: 'extra_information_about_the_defect',
                        label: this.translate.instant('create-claim.documentation.extra_information_about_the_defect'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.extra_information_about_the_defect'),
                        isRequired: true,
                        value: this.findValueOfType('extra_information_about_the_defect', claimDocumentation.additionalInfo)
                    },
                    // {
                    //     id: 'how_was_the_cleaning_process',
                    //     label: this.translate.instant('create-claim.documentation.how_was_the_cleaning_process'),
                    //     englishLabel: this.engTranslate.instant('create-claim.documentation.how_was_the_cleaning_process'),
                    //     isRequired: true,
                    //     value: this.findValueOfType('how_was_the_cleaning_process', claimDocumentation.additionalInfo)
                    // }
                ]

            }
        } else if(claimType === 'marks') {
            requiredDocuments = {
                checks: [],
                documents: [
                    {
                        id: 'purchase_invoice',
                        label: this.translate.instant('create-claim.documentation.purchase_invoice'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.purchase_invoice'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: this.shouldAskPurchaseOrder(hasWarranty, claimPlace),
                        document: this.findDocumentOfType('purchase_invoice', claimDocumentation.documents)
                    }, {
                        id: 'defect_picture',
                        label: this.translate.instant('create-claim.documentation.defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.defect_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('defect_picture', claimDocumentation.documents)
                    }, {
                        id: 'comparison_defect_picture',
                        label: this.translate.instant('create-claim.documentation.comparison_defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.comparison_defect_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('comparison_defect_picture', claimDocumentation.documents)
                    }
                ],
                additionalInfo: [
                    {
                        id: 'extra_information_about_the_stains',
                        label: this.translate.instant('create-claim.documentation.extra_information_about_the_defect'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.extra_information_about_the_defect'),
                        isRequired: true,
                        value: this.findValueOfType('extra_information_about_the_stains', claimDocumentation.additionalInfo)
                    }
                ]

            }
        } else if(claimType === 'stains') {
            requiredDocuments = {
                checks: [],
                documents: [
                    {
                        id: 'purchase_invoice',
                        label: this.translate.instant('create-claim.documentation.purchase_invoice'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.purchase_invoice'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: this.shouldAskPurchaseOrder(hasWarranty, claimPlace),
                        document: this.findDocumentOfType('purchase_invoice', claimDocumentation.documents)
                    }, {
                        id: 'defect_picture',
                        label: this.translate.instant('create-claim.documentation.defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.defect_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('defect_picture', claimDocumentation.documents)
                    }, {
                        id: 'comparison_defect_picture',
                        label: this.translate.instant('create-claim.documentation.comparison_defect_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.comparison_defect_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('comparison_defect_picture', claimDocumentation.documents)
                    }, {
                        id: 'cleaning_products_utilized_picture',
                        label: this.translate.instant('create-claim.documentation.cleaning_products_utilized_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.cleaning_products_utilized_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('cleaning_products_utilized_picture', claimDocumentation.documents)
                    }
                ],
                additionalInfo: [
                    {
                        id: 'please_describe_the_stain',
                        label: this.translate.instant('create-claim.documentation.please_describe_the_stain'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.please_describe_the_stain'),
                        isRequired: true,
                        value: this.findValueOfType('please_describe_the_stain', claimDocumentation.additionalInfo)
                    }, {
                        id: 'how_did_the_stain_occur',
                        label: this.translate.instant('create-claim.documentation.how_did_the_stain_occur'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.how_did_the_stain_occur'),
                        isRequired: true,
                        value: this.findValueOfType('how_did_the_stain_occur', claimDocumentation.additionalInfo)
                    }, {
                        id: 'describe_the_cleaning_process',
                        label: this.translate.instant('create-claim.documentation.describe_the_cleaning_process'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.describe_the_cleaning_process'),
                        isRequired: true,
                        value: this.findValueOfType('describe_the_cleaning_process', claimDocumentation.additionalInfo)
                    }
                ]

            }
        } else if(claimType === 'organoleptic') {
            requiredDocuments = {
                checks: [],
                documents: [
                    {
                        id: 'purchase_invoice',
                        label: this.translate.instant('create-claim.documentation.purchase_invoice'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.purchase_invoice'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: this.shouldAskPurchaseOrder(hasWarranty, claimPlace),
                        document: this.findDocumentOfType('purchase_invoice', claimDocumentation.documents)
                    },
                    {
                        id: 'general_material_picture',
                        label: this.translate.instant('create-claim.documentation.general_material_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.general_material_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('general_material_picture', claimDocumentation.documents)
                    }
                ],
                additionalInfo: [
                    {
                        id: 'please_describe_the_organoleptic_defect',
                        label: this.translate.instant('create-claim.documentation.please_describe_the_organoleptic_defect'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.please_describe_the_organoleptic_defect'),
                        isRequired: true,
                        value: this.findValueOfType('please_describe_the_organoleptic_defect', claimDocumentation.additionalInfo)
                    }
                ]

            }
        } else if(claimPlace === 'in_shop' && claimType === 'broken'){
            requiredDocuments = {
                checks: [],
                documents: [
                    {
                        id: 'surface_on_cutting_board_picture',
                        label: this.translate.instant('create-claim.documentation.surface_on_cutting_board_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.surface_on_cutting_board_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('surface_on_cutting_board_picture', claimDocumentation.documents)
                    }, {
                        id: 'break_detail_on_cutting_board_picture',
                        label: this.translate.instant('create-claim.documentation.break_detail_on_cutting_board_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.break_detail_on_cutting_board_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('break_detail_on_cutting_board_picture', claimDocumentation.documents)
                    }, {
                        id: 'surface_tag_picture',
                        label: this.translate.instant('create-claim.documentation.surface_tag_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.surface_tag_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('surface_tag_picture', claimDocumentation.documents)
                    }, {
                        id: 'tool_used_when_break_happened_picture',
                        label: this.translate.instant('create-claim.documentation.tool_used_when_break_happened_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.tool_used_when_break_happened_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('tool_used_when_break_happened_picture', claimDocumentation.documents)
                    }, {
                        id: 'settings_on_cutting_machine_screen_picture',
                        label: this.translate.instant('create-claim.documentation.settings_on_cutting_machine_screen_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.settings_on_cutting_machine_screen_picture'),
                        isRequired: false,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        document: this.findDocumentOfType('settings_on_cutting_machine_screen_picture', claimDocumentation.documents)
                    },
                ],
                additionalInfo: [
                    {
                        id: 'cut_settings',
                        label: this.translate.instant('create-claim.documentation.cut_settings'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.cut_settings'),
                        isRequired: true,
                        value: this.findValueOfType('cut_settings', claimDocumentation.additionalInfo)
                    }
                ]
            }
        } else if(claimPlace === 'in_transit_center_to_shop' || claimPlace === 'in_transit_hq_to_shop'){
            requiredDocuments = {
                checks: [],
                documents: [
                    {
                        id: 'land_sea_transport_document',
                        label: this.translate.instant('create-claim.documentation.land_sea_transport_document'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.land_sea_transport_document'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount >= 3000,
                        document: this.findDocumentOfType('land_sea_transport_document', claimDocumentation.documents)
                    },
                    {
                        id: 'delivery_note_with_reservations',
                        label: this.translate.instant('create-claim.documentation.delivery_note_with_reservations'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.delivery_note_with_reservations'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount >= 3000,
                        document: this.findDocumentOfType('delivery_note_with_reservations', claimDocumentation.documents)
                    },
                    {
                        id: 'damage_claims_document',
                        label: this.translate.instant('create-claim.documentation.damage_claims_document'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.damage_claims_document'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount >= 3000,
                        document: this.findDocumentOfType('damage_claims_document', claimDocumentation.documents),
                        extraDownloadableDocumentLabel: this.translate.instant('create-claim.documentation.download_damage_claims_document'),
                        extraDownloadableDocumentLink: this.docMapping.getClaimFormForLanguage(language)
                    },
                    {
                        id: 'unloaded_unit_picture',
                        label: this.translate.instant('create-claim.documentation.unloaded_unit_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.unloaded_unit_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount >= 3000,
                        document: this.findDocumentOfType('unloaded_unit_picture', claimDocumentation.documents)
                    },
                    {
                        id: 'damages_picture',
                        label: this.translate.instant('create-claim.documentation.damages_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.damages_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount >= 3000,
                        document: this.findDocumentOfType('damages_picture', claimDocumentation.documents)
                    },
                    {
                        id: 'tools_used_picture',
                        label: this.translate.instant('create-claim.documentation.tools_used_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.tools_used_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount >= 3000,
                        document: this.findDocumentOfType('tools_used_picture', claimDocumentation.documents)
                    },
                    {
                        id: 'product_packaging_picture',
                        label: this.translate.instant('create-claim.documentation.product_packaging_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.product_packaging_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount >= 3000,
                        document: this.findDocumentOfType('product_packaging_picture', claimDocumentation.documents)
                    },
                    {
                        id: 'mesh_status_picture',
                        label: this.translate.instant('create-claim.documentation.mesh_status_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.mesh_status_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount >= 3000 && hasMesh,
                        document: this.findDocumentOfType('mesh_status_picture', claimDocumentation.documents)
                    },
                    {
                        id: 'unit_inside_the_truck_picture',
                        label: this.translate.instant('create-claim.documentation.unit_inside_the_truck_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.unit_inside_the_truck_picture'),
                        isRequired: false,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount >= 3000,
                        document: this.findDocumentOfType('unit_inside_the_truck_picture', claimDocumentation.documents)
                    },
                    {
                        id: 'unloaded_unit_before_unpacking_picture',
                        label: this.translate.instant('create-claim.documentation.unloaded_unit_before_unpacking_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.unloaded_unit_before_unpacking_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount < 3000,
                        document: this.findDocumentOfType('unloaded_unit_before_unpacking_picture', claimDocumentation.documents)
                    },
                    {
                        id: 'damages_picture',
                        label: this.translate.instant('create-claim.documentation.damages_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.damages_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount < 3000,
                        document: this.findDocumentOfType('damages_picture', claimDocumentation.documents)
                    },
                    {
                        id: 'tools_used_picture',
                        label: this.translate.instant('create-claim.documentation.tools_used_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.tools_used_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount < 3000,
                        document: this.findDocumentOfType('tools_used_picture', claimDocumentation.documents)
                    },
                    {
                        id: 'product_packaging_picture',
                        label: this.translate.instant('create-claim.documentation.product_packaging_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.product_packaging_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount < 3000,
                        document: this.findDocumentOfType('product_packaging_picture', claimDocumentation.documents)
                    },
                    {
                        id: 'mesh_status_picture',
                        label: this.translate.instant('create-claim.documentation.mesh_status_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.mesh_status_picture'),
                        isRequired: true,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount < 3000 && hasMesh,
                        document: this.findDocumentOfType('mesh_status_picture', claimDocumentation.documents)
                    },
                    // {
                    //     id: 'copy_of_delivery_note',
                    //     label: this.translate.instant('create-claim.documentation.copy_of_delivery_note'),
                    //     isRequired: false,
                    //     requiresChecked: [],
                    //     requiresUnchecked: [],
                    //     shouldShow: totalAmount < 3000,
                    //     document: this.findDocumentOfType('copy_of_delivery_note', claimDocumentation.documents)
                    // },
                    {
                        id: 'exterior_of_truck_picture',
                        label: this.translate.instant('create-claim.documentation.exterior_of_truck_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.exterior_of_truck_picture'),
                        isRequired: false,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount < 3000,
                        document: this.findDocumentOfType('exterior_of_truck_picture', claimDocumentation.documents)
                    },
                    {
                        id: 'unit_inside_the_truck_picture',
                        label: this.translate.instant('create-claim.documentation.unit_inside_the_truck_picture'),
                        englishLabel: this.engTranslate.instant('create-claim.documentation.unit_inside_the_truck_picture'),
                        isRequired: false,
                        requiresChecked: [],
                        requiresUnchecked: [],
                        shouldShow: totalAmount < 3000,
                        document: this.findDocumentOfType('unit_inside_the_truck_picture', claimDocumentation.documents)
                    },
                ],
                additionalInfo: []
            }
        }
        // else if(claimPlace === 'in_transit_shop_to_client'){
        //     requiredDocuments = {
        //         checks: [],
        //         documents: [
        //             {
        //                 id: 'unloaded_unit_before_unpacking_picture',
        //                 label: this.translate.instant('create-claim.documentation.unloaded_unit_before_unpacking_picture'),
        //                 englishLabel: this.engTranslate.instant('create-claim.documentation.unloaded_unit_before_unpacking_picture'),
        //                 isRequired: true,
        //                 requiresChecked: [],
        //                 requiresUnchecked: [],
        //                 document: this.findDocumentOfType('unloaded_unit_before_unpacking_picture', claimDocumentation.documents)
        //             },
        //             {
        //                 id: 'damages_picture',
        //                 label: this.translate.instant('create-claim.documentation.damages_picture'),
        //                 englishLabel: this.engTranslate.instant('create-claim.documentation.damages_picture'),
        //                 isRequired: true,
        //                 requiresChecked: [],
        //                 requiresUnchecked: [],
        //                 document: this.findDocumentOfType('damages_picture', claimDocumentation.documents)
        //             },
        //             {
        //                 id: 'tools_used_picture',
        //                 label: this.translate.instant('create-claim.documentation.tools_used_picture'),
        //                 englishLabel: this.engTranslate.instant('create-claim.documentation.tools_used_picture'),
        //                 isRequired: true,
        //                 requiresChecked: [],
        //                 requiresUnchecked: [],
        //                 document: this.findDocumentOfType('tools_used_picture', claimDocumentation.documents)
        //             },
        //             {
        //                 id: 'product_packaging_picture',
        //                 label: this.translate.instant('create-claim.documentation.product_packaging_picture'),
        //                 englishLabel: this.engTranslate.instant('create-claim.documentation.product_packaging_picture'),
        //                 isRequired: true,
        //                 requiresChecked: [],
        //                 requiresUnchecked: [],
        //                 document: this.findDocumentOfType('product_packaging_picture', claimDocumentation.documents)
        //             },
        //             {
        //                 id: 'mesh_status_picture',
        //                 label: this.translate.instant('create-claim.documentation.mesh_status_picture'),
        //                 englishLabel: this.engTranslate.instant('create-claim.documentation.mesh_status_picture'),
        //                 isRequired: true,
        //                 requiresChecked: [],
        //                 requiresUnchecked: [],
        //                 shouldShow: hasMesh,
        //                 document: this.findDocumentOfType('mesh_status_picture', claimDocumentation.documents)
        //             },
        //             {
        //                 id: 'exterior_of_truck_picture',
        //                 label: this.translate.instant('create-claim.documentation.exterior_of_truck_picture'),
        //                 englishLabel: this.engTranslate.instant('create-claim.documentation.exterior_of_truck_picture'),
        //                 isRequired: false,
        //                 requiresChecked: [],
        //                 requiresUnchecked: [],
        //                 document: this.findDocumentOfType('exterior_of_truck_picture', claimDocumentation.documents)
        //             },
        //             {
        //                 id: 'unit_inside_the_truck_picture',
        //                 label: this.translate.instant('create-claim.documentation.unit_inside_the_truck_picture'),
        //                 englishLabel: this.engTranslate.instant('create-claim.documentation.unit_inside_the_truck_picture'),
        //                 isRequired: false,
        //                 requiresChecked: [],
        //                 requiresUnchecked: [],
        //                 document: this.findDocumentOfType('unit_inside_the_truck_picture', claimDocumentation.documents)
        //             }
        //         ],
        //         additionalInfo: []
        //     }
        // }

        return requiredDocuments;
    }

    findDocumentOfType(type: ClaimDocumentType, documents: ClaimDocument[]): ClaimDocument | undefined {
        for (let document of documents) {
            if(document.type === type) {
                return document;
            }
        }
        return undefined;
    }

    findValueOfType(type: string, values: {type?: string, value?: string}[]): string | undefined {
        for (let value of values) {
            if(value.type === type) {
                return value.value;
            }
        }
        return undefined;
    }

    findAdditionalValueOfType(type: string, values: {type?: string, additionalInfo?: {value?: string}}[]): string | undefined {
        for (let value of values) {
            if(value.type === type) {
                return value.additionalInfo?.value;
            }
        }
        return undefined;
    }

    private findCheckOfType(type: string, checks: { type?: string; value?: string }[]) {
        for (let value of checks) {
            if(value.type === type) {
                if(value.value === 'true') return true;
                if(value.value === 'false') return false;
                return undefined
            }
        }
        return undefined;
    }

    private shouldAskPurchaseOrder(hasWarranty?: boolean, claimPlace?: ClaimPlaceType): boolean {
        return !hasWarranty && claimPlace === 'installation';
    }
}
